import { useEffect, useState, type ComponentType } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button as UIButton } from "@/components/ui/button";
import { Input as UIInput } from "@/components/ui/input";
import { Confirm, useGuard } from "./dialogs";
import { LoadError, Pill, SectionTitle, Skeleton, useLoad, when, type Tone } from "./ui";

const Button = UIButton as ComponentType<any>;
const Input = UIInput as ComponentType<any>;

// Who may start using Remy. Approving never texts anyone: the person starts by texting Remy.

const STATE: Record<string, { text: string; tone: Tone }> = {
  requested: { text: "Waiting for your OK", tone: "watch" },
  invited: { text: "Invited, not started", tone: "neutral" },
  declined: { text: "Invite withdrawn", tone: "neutral" },
  left: { text: "Deleted their account", tone: "neutral" },
  unmatched: { text: "Needs a look", tone: "watch" },
};
const ORDER = ["requested", "invited", "left", "declined", "unmatched"];

export default function AdminRequests() {
  const { data, error, loading, reload } = useLoad<any[]>("/admin/roster");
  const [form, setForm] = useState({ first_name: "", last_name: "", mobile_number: "" });
  const [busy, setBusy] = useState(false);
  const guard = useGuard();
  const [removing, setRemoving] = useState<any>(null);
  const [withdrawing, setWithdrawing] = useState<any>(null);
  const [smsNumber, setSmsNumber] = useState("");
  useEffect(() => {
    api.get("/config").then((r) => setSmsNumber(r.data?.sms_number || "")).catch(() => {});
  }, []);

  const inviteMessage = (firstName: string) => {
    const d = smsNumber.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
    const pretty = d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : smsNumber;
    return `Hi${firstName ? " " + firstName : ""}, I set you up on Remy, my AI agent. ` +
      `Text "Hello Remy" to ${pretty} and reply YES when it asks. Then save the number as Remy.`;
  };
  const copyInvite = async (firstName: string) => {
    try {
      await navigator.clipboard.writeText(inviteMessage(firstName));
      toast.success("Invite message copied. Send it from your own phone.");
    } catch {
      toast.message(inviteMessage(firstName));
    }
  };
  const send = async (id: string, action: "approve" | "revoke") => {
    await api.post(`/admin/invites/${id}/${action}`);
    toast.success(action === "approve" ? "Approved. Copy the invite and send it yourself." : "Invite withdrawn.");
    await reload();
  };
  const act = (id: string, action: "approve" | "revoke") =>
    send(id, action).catch((e) => toast.error(apiError(e)));
  const add = async () => {
    setBusy(true);
    try {
      await api.post("/admin/invites", form);
      setForm({ first_name: "", last_name: "", mobile_number: "" });
      toast.success("Invited. Copy the invite and send it from your own phone.");
      await reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const invites = (data || []).filter((r) => r.kind === "invite")
    .sort((a, b) => ORDER.indexOf(a.state) - ORDER.indexOf(b.state));

  return (
    <div data-testid="admin-requests">
      <SectionTitle kicker="Requests" title="Invite someone"
        note="Remy is invite-only. Inviting never sends a text: you send them the invite yourself." />
      <div className="rounded-3xl border border-border bg-card p-5" data-testid="invite-form">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Input className="h-11 rounded-2xl text-base" placeholder="First name" value={form.first_name}
            data-testid="invite-first-name" aria-label="First name"
            onChange={(e: any) => setForm({ ...form, first_name: e.target.value })} />
          <Input className="h-11 rounded-2xl text-base" placeholder="Last name (optional)" value={form.last_name}
            data-testid="invite-last-name" aria-label="Last name"
            onChange={(e: any) => setForm({ ...form, last_name: e.target.value })} />
        </div>
        <Input className="mt-2 h-11 rounded-2xl text-base" placeholder="Mobile number" value={form.mobile_number}
          inputMode="tel" data-testid="invite-mobile" aria-label="Mobile number"
          onChange={(e: any) => setForm({ ...form, mobile_number: e.target.value })} />
        <Button onClick={add} className="mt-3 h-11 w-full rounded-full" data-testid="invite-add"
          disabled={busy || !form.first_name.trim() || !form.mobile_number.trim()}>
          Invite
        </Button>
      </div>

      <SectionTitle title="Requests and invitations" />
      {loading && !data ? <Skeleton /> : error && !data ? <LoadError message={error} onRetry={reload} /> : (
        <div className="space-y-2">
          {invites.map((r) => {
            const st = STATE[r.state] || STATE.unmatched;
            const first = (r.name || "").split(" ")[0];
            const can = (a: string) => (r.actions || []).includes(a);
            return (
              <div key={r.key} className="rounded-2xl border border-border bg-card px-4 py-3"
                data-testid={`invite-${r.invite_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold">{r.name}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {r.phone}
                      {r.state === "requested" && r.days_waiting != null
                        ? ` · asked ${r.days_waiting === 0 ? "today" : `${r.days_waiting} days ago`}` : ""}
                      {r.state === "invited" && r.invited_at ? ` · invited ${when(r.invited_at)}` : ""}
                      {r.state === "left" && r.left_at ? ` · left ${when(r.left_at)}` : ""}
                      {r.sms_consent ? " · agreed to texts" : ""}
                    </div>
                  </div>
                  <span className="flex shrink-0 flex-col items-end gap-1">
                    <Pill tone={st.tone}>{st.text}</Pill>
                    {(r.flags || []).includes("was_removed") && <Pill tone="attention">Removed before</Pill>}
                  </span>
                </div>
                {(r.actions || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {can("approve") && (
                      <Button className="h-11 rounded-full px-5" onClick={() => act(r.invite_id, "approve")}
                        data-testid={`approve-${r.invite_id}`}>
                        {r.state === "declined" ? "Invite again" : "Approve"}
                      </Button>
                    )}
                    {can("copy_invite") && smsNumber && (
                      <Button className="h-11 rounded-full px-5" onClick={() => copyInvite(first)}
                        data-testid={`copy-invite-${r.invite_id}`}>
                        Copy invite
                      </Button>
                    )}
                    {can("revoke") && (
                      <Button variant="outline" className="h-11 rounded-full px-5"
                        onClick={() => setWithdrawing(r)}>
                        Withdraw
                      </Button>
                    )}
                    {can("remove") && (
                      <Button variant="ghost" className="h-11 rounded-full px-4 text-muted-foreground"
                        onClick={() => setRemoving(r)} data-testid={`remove-invite-${r.invite_id}`}>
                        Remove
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {invites.length === 0 && (
            <p className="text-sm text-muted-foreground">No requests or open invitations.</p>
          )}
        </div>
      )}

      <Confirm open={!!withdrawing} onClose={() => setWithdrawing(null)} action="Withdraw"
        title={withdrawing ? `Withdraw ${withdrawing.name}'s invitation?` : ""}
        body="They won't be able to start with Remy until you invite them again. Nothing is sent to them."
        run={async () => { await send(withdrawing.invite_id, "revoke"); }} />

      <Confirm open={!!removing} onClose={() => setRemoving(null)} action="Remove" danger
        title={removing ? `Remove ${removing.name}?` : ""}
        body="This takes them off the list completely. They can ask again from the website. Nothing is sent to them."
        run={async () => {
          await guard(() => api.post(`/admin/invites/${removing.invite_id}/remove`));
          toast.success("Removed");
          await reload();
        }} />
    </div>
  );
}
